import { defineStore } from 'pinia'
import { computed, ref } from 'vue'
import {
  navigationApi,
  type NavigationPayload,
  type NavigationRouteRow,
} from '@/features/auth/api/navigation.api'
import { localizedCatalogLabel } from '@/shared/utils/localized-catalog-label'

export const useNavigationStore = defineStore('navigation', () => {
  const payload = ref<NavigationPayload | null>(null)
  const isLoading = ref(false)
  const isLoaded = ref(false)
  let fetchPromise: Promise<void> | null = null
  let generation = 0

  const menus = computed(() => payload.value?.menus ?? [])
  const routes = computed(() => payload.value?.routes ?? {})

  const visibleMenuKeys = computed(() =>
    menus.value.filter((row) => row.allowed).map((row) => row.menu_key),
  )

  function menuRow(menuKey: string) {
    return menus.value.find((row) => row.menu_key === menuKey) ?? null
  }

  function menuLabel(menuKey: string, locale: string, fallback?: string): string {
    const row = menuRow(menuKey)
    if (!row) return fallback ?? menuKey

    return localizedCatalogLabel(
      row.labels,
      locale,
      row.label || fallback || menuKey,
      { enFallback: 'en' },
    )
  }

  function parentMenuKey(menuKey: string): string | null {
    const row = menuRow(menuKey)
    return row?.parent_menu_key ?? null
  }

  function isMenuAccessible(menuKey: string, seen: ReadonlySet<string> = new Set()): boolean {
    if (seen.has(menuKey)) return false
    const row = menuRow(menuKey)
    // Fail closed until navigation payload is loaded (avoids first-paint false allow).
    if (!row) return false
    if (!row.allowed || !row.globally_visible) return false
    const parentKey = parentMenuKey(menuKey)
    if (!parentKey) return true
    const next = new Set(seen)
    next.add(menuKey)
    return isMenuAccessible(parentKey, next)
  }

  function routeAccess(routeName: string | undefined): NavigationRouteRow | null {
    if (!routeName) return null
    return routes.value[routeName] ?? null
  }

  function firstAccessibleRouteName(preferred: string[] = []): string | null {
    for (const routeName of preferred) {
      const access = routeAccess(routeName)
      if (access?.allowed && isMenuAccessible(access.menu_key)) return routeName
    }

    const orderedMenus = [...menus.value].sort((left, right) => left.sort_order - right.sort_order)
    for (const menu of orderedMenus) {
      const routeName = menu.default_route_name
      if (!routeName) continue
      if (!routeAccess(routeName)?.allowed) continue
      if (!isMenuAccessible(menu.menu_key)) continue
      return routeName
    }

    return null
  }

  async function fetchNavigation(force = false) {
    if (fetchPromise && !force) return fetchPromise
    if (isLoaded.value && !force) return
    const requestGeneration = force ? ++generation : generation
    isLoading.value = true
    const request = navigationApi
      .fetchNavigation()
      .then((response) => {
        if (requestGeneration !== generation) return
        payload.value = response.data
        isLoaded.value = true
      })
      .finally(() => {
        if (fetchPromise === request) {
          isLoading.value = false
          fetchPromise = null
        }
      })
    fetchPromise = request
    return request
  }

  function invalidate() {
    generation += 1
    payload.value = null
    isLoaded.value = false
    isLoading.value = false
    fetchPromise = null
  }

  return {
    payload,
    menus,
    routes,
    isLoading,
    isLoaded,
    visibleMenuKeys,
    menuLabel,
    menuRow,
    parentMenuKey,
    isMenuAccessible,
    firstAccessibleRouteName,
    routeAccess,
    fetchNavigation,
    invalidate,
  }
})
